import { useEffect } from "react"
import { QueryClientProvider } from "@tanstack/react-query"
import { RouterProvider, createRouter } from "@tanstack/react-router"
import {
  StatsigProvider,
  useClientAsyncInit,
} from "@statsig/react-bindings"
import {
  setStatsigClient,
  getStatsigClientKey,
  getStatsigEnvironment,
} from "@/lib/telemetry/statsig"
import { trackEvent, Events } from "@/lib/telemetry/analytics"
import { routeTree } from "./routeTree.gen"
import { queryClient } from "./lib/query-client"

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  context: { queryClient },
})

router.subscribe("onResolved", ({ toLocation }) => {
  trackEvent(Events.PAGE_VIEW, { path: toLocation.pathname })
})

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

const statsigKey = getStatsigClientKey()
const statsigEnv = getStatsigEnvironment()

function AppContent() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  )
}

function AppWithStatsig() {
  const { client, isLoading } = useClientAsyncInit(
    statsigKey,
    { userID: "anonymous" },
    { environment: { tier: statsigEnv } },
  )

  useEffect(() => {
    if (!isLoading) {
      setStatsigClient(client)
    }
  }, [client, isLoading])

  return (
    <StatsigProvider client={client}>
      <AppContent />
    </StatsigProvider>
  )
}

export function App() {
  if (statsigKey) {
    return <AppWithStatsig />
  }

  return <AppContent />
}
